import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  confirmed:  'bg-green-100 text-green-700',
  pending:    'bg-yellow-100 text-yellow-700',
  active:     'bg-teal-100 text-teal-700',
  completed:  'bg-blue-100 text-blue-700',
  cancelled:  'bg-gray-100 text-gray-600',
  rejected:   'bg-red-100 text-red-700',
};

export default function AdminBookings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const statusLabel = (status) => ({
    confirmed: t('admin.statusConfirmed'),
    pending: t('admin.statusPending'),
    active: t('admin.statusActive', 'En curso'),
    completed: t('admin.statusCompleted'),
    cancelled: t('admin.statusCancelled'),
    rejected: t('admin.statusRejected'),
  }[status] || status);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/bookings`, { credentials: 'include' })
      .then(r => { if (r.status === 401 || r.status === 403) navigate('/login'); return r.json(); })
      .then(data => { setBookings(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [navigate]);

  const filtered = bookings.filter(b =>
    !search ||
    b.booking_code?.toLowerCase().includes(search.toLowerCase()) ||
    b.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.client_email?.toLowerCase().includes(search.toLowerCase())
  );

  // Paginación client-side: la tabla cargaba TODAS las reservas en memoria y
  // las pintaba de una vez — con cientos/miles de filas eso es un DOM enorme
  // y un re-render lento en cada tecla del buscador. 25 filas por página.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <AdminTabs />
      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <button onClick={() => navigate('/admin/dashboard')} className="mb-6 text-sm text-gray-500 hover:text-[#134168] transition-colors">
          {t('admin.backToPanelShort')}
        </button>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-heading font-bold text-[#134168]">{t('admin.bookingMgmt')}</h1>
          <span className="text-sm text-gray-500">{t('admin.totalBookingsCount', { n: bookings.length })}</span>
        </div>

        <input
          type="text"
          placeholder={t('admin.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full mb-6 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168] text-sm"
        />

        {loading ? (
          <AdminLoadingScreen chrome={false} className="py-16" message={t('admin.loadingBookings', 'Cargando reservas...')} />
        ) : (
          <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {[t('admin.code'), t('admin.client'), t('admin.dateLabel'), t('admin.hoursLabel'), t('admin.totalLabel'), t('admin.statusLabel'), t('admin.createdLabel')].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-gray-400">{t('admin.noBookings')}</td></tr>
                  ) : paged.map(b => (
                    <tr key={b.booking_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-mono font-medium text-xs">{b.booking_code}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium">{b.client_name}</p>
                        <p className="text-xs text-gray-400">{b.client_email}</p>
                        {b.delivery_requested && (
                          <div className="mt-1">
                            <span className="inline-block px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-semibold border border-blue-100">
                              🚚 Delivery
                            </span>
                            <p className="text-[10px] text-gray-500 mt-0.5 leading-tight truncate max-w-[150px]" title={b.delivery_address}>
                              {b.delivery_address}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{b.start_datetime ? new Date(b.start_datetime).toLocaleDateString(locale) : '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{b.end_hours}h</td>
                      <td className="px-4 py-3 font-bold text-[#134168]">{formatDOP(b.total_price)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                          {statusLabel(b.status)}
                        </span>
                        {b.refund && (
                          <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${b.refund.status === 'processed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {t('adminBookings.refund', 'Reembolso')}: {formatDOP(b.refund.amount)} ({b.refund.status === 'processed' ? t('adminBookings.refundProcessed', 'procesado') : t('adminBookings.refundPending', 'pendiente')})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{b.created_at ? new Date(b.created_at).toLocaleDateString(locale) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
                <span>{t('admin.pageOf', 'Página {{page}} de {{total}}', { page, total: totalPages })}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 rounded-full border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {t('admin.prevPage', 'Anterior')}
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 rounded-full border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    {t('admin.nextPage', 'Siguiente')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
